/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useMemo, useState } from 'react'

import { EuiButton, EuiSkeletonText, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject, AnyLinkConfiguration } from '@modules/project-api/types'
import type { ProjectType, ProjectID } from '@modules/ui-types/projects'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'

import { useProjectCpsContext } from './ProjectCpsContext'
import { useUnlinkProjects } from './wizard/hooks/useUnlinkProjects'
import ProjectsTable from './ProjectsTable'
import UnlinkProjectsModal from './UnlinkProjectsModal'

type Props = {
  linkedProjects: AnyLinkConfiguration['projects']
  projectId: ProjectID
  projectType: ProjectType
  projectName: string
}

const LinkedProjectsList: React.FunctionComponent<Props> = ({
  linkedProjects,
  projectId,
  projectType,
  projectName,
}) => {
  const [selectedProjects, setSelectedProjects] = useState<AnyProject[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { unlinkProjects, isLoading: isUnlinking, error: unlinkError } = useUnlinkProjects()

  const {
    useListElasticsearchProjectsQuery,
    useListObservabilityProjectsQuery,
    useListSecurityProjectsQuery,
  } = useProjectCpsContext()

  const elasticsearchQuery = useListElasticsearchProjectsQuery({ include_deleted: true })
  const observabilityQuery = useListObservabilityProjectsQuery({ include_deleted: true })
  const securityQuery = useListSecurityProjectsQuery({ include_deleted: true })

  const queries = [elasticsearchQuery, observabilityQuery, securityQuery]
  const isLoading = queries.some((query) => query.isLoading)

  const linkedProjectIds = Object.keys(linkedProjects)

  // Filter only the linked projects from all projects
  const items: AnyProject[] = useMemo(() => {
    const allProjects: AnyProject[] = []

    if (elasticsearchQuery.data) {
      allProjects.push(...elasticsearchQuery.data.items)
    }

    if (observabilityQuery.data) {
      allProjects.push(...observabilityQuery.data.items)
    }

    if (securityQuery.data) {
      allProjects.push(...securityQuery.data.items)
    }

    return allProjects.filter((project) => linkedProjectIds.includes(project.id))
  }, [elasticsearchQuery.data, observabilityQuery.data, securityQuery.data, linkedProjectIds])

  // TODO: push into items the IDs of the linked projects that no longer exist

  const handleUnlinkClick = () => {
    setIsModalVisible(true)
  }

  const handleCancelModal = () => {
    setIsModalVisible(false)
  }

  const handleConfirmUnlink = async () => {
    const projectIdsToUnlink = selectedProjects.map((p) => p.id)

    try {
      await unlinkProjects({
        projectId,
        projectType,
        projectIdsToUnlink,
      })
      setSelectedProjects([])
      setIsModalVisible(false)
    } catch (error) {
      // Error is displayed via unlinkError
      // Close modal so user can see the error callout
      setIsModalVisible(false)
    }
  }

  const handleSelectAll = () => {
    setSelectedProjects(items)
  }

  const handleSelectNone = () => {
    setSelectedProjects([])
  }

  const selection = {
    selectable: () => true,
    selectableMessage: () => 'Select this project',
    onSelectionChange: (selected: AnyProject[]) => {
      setSelectedProjects(selected)
    },
    initialSelected: selectedProjects,
    selected: selectedProjects,
  }

  if (isLoading) {
    return <EuiSkeletonText />
  }

  return (
    <Fragment>
      {unlinkError && (
        <Fragment>
          <ProjectApiErrorCallout error={unlinkError} />
          <EuiSpacer size='m' />
        </Fragment>
      )}

      <ProjectsTable
        items={items}
        selectedProjects={selectedProjects}
        onSelectionChange={setSelectedProjects}
        onSelectAll={handleSelectAll}
        onSelectNone={handleSelectNone}
        selection={selection}
        showingTextType='linked'
        showSelectAllNone={false}
        actionButtons={
          <EuiButton
            color='danger'
            iconType='unlink'
            onClick={handleUnlinkClick}
            size='s'
            data-test-subj='cps-linkedProjectsList-unlinkButton'
          >
            <FormattedMessage id='linkedProjects.unlinkButton' defaultMessage='Unlink' />
          </EuiButton>
        }
      />

      {isModalVisible && (
        <UnlinkProjectsModal
          selectedProjects={selectedProjects}
          projectName={projectName}
          isUnlinking={isUnlinking}
          onCancel={handleCancelModal}
          onConfirm={handleConfirmUnlink}
        />
      )}
    </Fragment>
  )
}

export default LinkedProjectsList
